import { useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client/react";

import {
  type CellState,
  type InviteGrantMatrix,
  RoleInviteGrantMatrixDocument,
  SetRoleInviteGrantDocument,
} from "#app/lib/data";

// Apollo plumbing for the Permissions page's "Invite Permissions"
// matrix. Returns the query result + a toggle that writes
// optimistically against the local cache so the Switch UI doesn't lag
// the round-trip.

export type { InviteGrantMatrix };

export function useInviteGrantMatrix(workspaceId: string) {
  const queryOptions = useMemo(
    () => ({ variables: { workspaceId }, skip: !workspaceId }),
    [workspaceId],
  );
  const { data, loading } = useQuery(
    RoleInviteGrantMatrixDocument,
    queryOptions,
  );
  const [setRoleInviteGrant] = useMutation(SetRoleInviteGrantDocument);

  const toggle = useCallback(
    (inviterRoleId: string, invitableRoleId: string, granted: boolean) => {
      if (!workspaceId) return;
      void setRoleInviteGrant({
        variables: { input: { inviterRoleId, invitableRoleId, granted } },
        optimisticResponse: { setRoleInviteGrant: true },
        update: (cache) => {
          const existing = cache.readQuery({
            query: RoleInviteGrantMatrixDocument,
            variables: { workspaceId },
          });
          if (!existing) return;
          const nextState = (granted ? "yes" : "no") as CellState;
          const prevGrants = existing.roleInviteGrantMatrix.grants;
          const hasRow = prevGrants.some(
            (g) =>
              g.inviterRoleId === inviterRoleId &&
              g.invitableRoleId === invitableRoleId,
          );
          // The matrix only carries grant rows that exist server-side, so
          // a cell that was never set has NO row here. Mapping alone would
          // then be a no-op and the Switch would snap back to OFF — append
          // a fresh row in that case so the optimistic state sticks.
          const grants = hasRow
            ? prevGrants.map((g) =>
                g.inviterRoleId === inviterRoleId &&
                g.invitableRoleId === invitableRoleId
                  ? { ...g, state: nextState }
                  : g,
              )
            : [
                ...prevGrants,
                {
                  __typename: "RoleInviteGrantPair" as const,
                  inviterRoleId,
                  invitableRoleId,
                  state: nextState,
                },
              ];
          cache.writeQuery({
            query: RoleInviteGrantMatrixDocument,
            variables: { workspaceId },
            data: {
              ...existing,
              roleInviteGrantMatrix: {
                ...existing.roleInviteGrantMatrix,
                grants,
              },
            },
          });
        },
      });
    },
    [workspaceId, setRoleInviteGrant],
  );

  return {
    loading,
    matrix: data?.roleInviteGrantMatrix as InviteGrantMatrix | undefined,
    toggle,
  };
}
